
import { useState, useEffect } from "react";
import DashboardLayout from "@/components/dashboard/DashboardLayout";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Search, Download, DollarSign, CreditCard, FileText, TrendingUp, Filter } from "lucide-react";
import { Input } from "@/components/ui/input";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { Badge } from "@/components/ui/badge";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, PieChart, Pie, Cell } from 'recharts';
import { Label } from "@/components/ui/label";
import { getAllInstructorPayments } from "@/utils/firebase";
import { useNavigate } from "react-router-dom";

// Transaction type
interface Transaction {
  id: string;
  studentId: string;
  studentName: string;
  amount: number;
  paymentMethod: string;
  packageName: string;
  status: "completed" | "pending" | "refunded" | "failed";
  date: string;
}

// Instructor payment type
interface InstructorPayment {
  id: string;
  instructorName: string;
  studentName: string;
  totalAmount: number;
  amountPaid: number;
  amountRemaining: number;
  courseName: string;
  paymentStatus: 'unpaid' | 'partially_paid' | 'fully_paid';
}

// Revenue data for chart
const revenueData = [
  { name: 'Jan', revenue: 24500 },
  { name: 'Feb', revenue: 28700 },
  { name: 'Mar', revenue: 27900 },
  { name: 'Apr', revenue: 42000 },
  { name: 'May', revenue: 38400 },
  { name: 'Jun', revenue: 33800 },
  { name: 'Jul', revenue: 36000 },
  { name: 'Aug', revenue: 25400 },
  { name: 'Sep', revenue: 29300 },
  { name: 'Oct', revenue: 34200 },
  { name: 'Nov', revenue: 32500 },
  { name: 'Dec', revenue: 38100 },
];

// Package distribution data for pie chart
const packageData = [
  { name: 'Single Lessons', value: 25 },
  { name: '5 Lesson Package', value: 35 },
  { name: '10 Lesson Package', value: 30 },
  { name: 'Theory Test Prep', value: 10 },
];

const COLORS = ['#0088FE', '#00C49F', '#FFBB28', '#FF8042'];

const FinanceManagement = () => {
  const navigate = useNavigate();
  const [searchTerm, setSearchTerm] = useState("");
  const [activeTab, setActiveTab] = useState("overview");
  const [dateRange, setDateRange] = useState("all");
  const [instructorPayments, setInstructorPayments] = useState<InstructorPayment[]>([]);
  const [loadingInstructorPayments, setLoadingInstructorPayments] = useState(false);

  // Sample transactions data
  const sampleTransactions: Transaction[] = [
    {
      id: "1",
      studentId: "student1",
      studentName: "Alex Johnson",
      amount: 249.99,
      paymentMethod: "Credit Card",
      packageName: "10 Lesson Package",
      status: "completed",
      date: "2025-04-16T14:30:00Z",
    },
    {
      id: "2",
      studentId: "student2",
      studentName: "Emma Williams",
      amount: 149.99,
      paymentMethod: "PayPal",
      packageName: "5 Lesson Package",
      status: "completed",
      date: "2025-04-15T11:20:00Z",
    },
    {
      id: "3",
      studentId: "student3",
      studentName: "Michael Brown",
      amount: 59.99,
      paymentMethod: "Credit Card",
      packageName: "Theory Test Prep",
      status: "completed",
      date: "2025-04-14T09:45:00Z",
    },
    {
      id: "4",
      studentId: "student4",
      studentName: "Jennifer Lee",
      amount: 49.99,
      paymentMethod: "Credit Card",
      packageName: "Single Lesson",
      status: "pending",
      date: "2025-04-19T10:00:00Z",
    },
    {
      id: "5",
      studentId: "student5",
      studentName: "Robert Garcia",
      amount: 249.99,
      paymentMethod: "PayPal",
      packageName: "10 Lesson Package",
      status: "refunded",
      date: "2025-04-12T13:15:00Z",
    },
  ];

  // In a real app, you would load transactions from localStorage or an API
  const getTransactions = (): Transaction[] => {
    const storedTransactions = localStorage.getItem("transactions");
    return storedTransactions ? JSON.parse(storedTransactions) : sampleTransactions;
  };

  const transactions = getTransactions();

  const filteredTransactions = transactions
    .filter(
      (transaction) =>
        transaction.studentName.toLowerCase().includes(searchTerm.toLowerCase()) ||
        transaction.packageName.toLowerCase().includes(searchTerm.toLowerCase()) ||
        transaction.paymentMethod.toLowerCase().includes(searchTerm.toLowerCase())
    )
    .sort((a, b) => new Date(b.date).getTime() - new Date(a.date).getTime());

  // Calculate financial metrics
  const totalRevenue = transactions
    .filter(t => t.status === "completed")
    .reduce((sum, transaction) => sum + transaction.amount, 0);

  const pendingRevenue = transactions
    .filter(t => t.status === "pending")
    .reduce((sum, transaction) => sum + transaction.amount, 0);

  const refundedAmount = transactions
    .filter(t => t.status === "refunded")
    .reduce((sum, transaction) => sum + transaction.amount, 0);
    
  // Calculate instructor payment metrics
  const totalInstructorPaid = instructorPayments.reduce((sum, payment) => sum + payment.amountPaid, 0);
  const totalInstructorPending = instructorPayments.reduce((sum, payment) => sum + payment.amountRemaining, 0);

  // Fetch instructor payments when tab changes to instructor
  useEffect(() => {
    const fetchInstructorPayments = async () => {
      if (activeTab !== "instructors") return;
      
      setLoadingInstructorPayments(true);
      try {
        const payments = await getAllInstructorPayments();
        console.log("Fetched instructor payments:", payments);
        setInstructorPayments(payments);
      } catch (error) {
        console.error("Error fetching instructor payments:", error);
      } finally {
        setLoadingInstructorPayments(false);
      }
    };
    
    fetchInstructorPayments();
  }, [activeTab]);

  const getStatusColor = (status: string) => {
    switch (status) {
      case "completed":
        return "bg-green-500";
      case "pending":
        return "bg-amber-500";
      case "refunded":
        return "bg-red-500";
      case "failed":
        return "bg-gray-500";
      default:
        return "bg-gray-500";
    }
  };
  
  const getInstructorStatusColor = (status: string) => {
    switch (status) {
      case "fully_paid":
        return "bg-green-500";
      case "partially_paid":
        return "bg-amber-500";
      case "unpaid":
        return "bg-gray-500";
      default:
        return "bg-gray-500";
    }
  };

  return (
    <DashboardLayout role="admin">
      <div className="mb-8 space-y-4">
        <h1 className="text-3xl font-bold text-gray-900">Finance Management</h1>
        <p className="text-gray-600">
          Track payments, manage financial transactions, and generate reports.
        </p>
      </div>

      <Tabs defaultValue="overview" className="space-y-6" onValueChange={setActiveTab}>
        <TabsList>
          
          <TabsTrigger value="instructors">Instructor Payments</TabsTrigger>
          <TabsTrigger value="reports">Reports</TabsTrigger>
        </TabsList>

        

        

        <TabsContent value="instructors" className="space-y-6">
          <div className="grid gap-6 grid-cols-1 sm:grid-cols-2 lg:grid-cols-3">
            <Card>
              <CardContent className="p-6">
                <div className="flex items-center">
                  <div className="mr-4 bg-green-100 p-3 rounded-full">
                    <DollarSign className="h-6 w-6 text-green-600" />
                  </div>
                  <div>
                    <p className="text-sm font-medium text-gray-500">Total Paid to Instructors</p>
                    <h3 className="text-2xl font-bold text-gray-900">
                      ${totalInstructorPaid.toFixed(2)}
                    </h3>
                  </div>
                </div>
              </CardContent>
            </Card>

            <Card>
              <CardContent className="p-6">
                <div className="flex items-center">
                  <div className="mr-4 bg-amber-100 p-3 rounded-full">
                    <CreditCard className="h-6 w-6 text-amber-600" />
                  </div>
                  <div>
                    <p className="text-sm font-medium text-gray-500">Pending Instructor Payments</p>
                    <h3 className="text-2xl font-bold text-gray-900">
                      ${totalInstructorPending.toFixed(2)}
                    </h3>
                  </div>
                </div>
              </CardContent>
            </Card>

            <Card>
              <CardContent className="p-6">
                <div className="flex items-center">
                  <div className="mr-4 bg-blue-100 p-3 rounded-full">
                    <FileText className="h-6 w-6 text-blue-600" />
                  </div>
                  <div>
                    <p className="text-sm font-medium text-gray-500">Instructor Payment Records</p>
                    <h3 className="text-2xl font-bold text-gray-900">
                      {instructorPayments.length}
                    </h3>
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>

          <Card>
            <CardHeader className="pb-1">
              <CardTitle>Instructor Payment Summary</CardTitle>
              <div className="flex justify-between items-center mt-4">
                <CardDescription>
                  View and manage payments to driving instructors
                </CardDescription>
                <Button onClick={() => navigate("/admin-dashboard/instructor-payments")}>
                  Manage Payments
                </Button>
              </div>
            </CardHeader>
            <CardContent>
              {loadingInstructorPayments ? (
                <div className="text-center py-8">
                  <p>Loading instructor payment data...</p>
                </div>
              ) : instructorPayments.length === 0 ? (
                <div className="text-center py-8">
                  <h3 className="text-lg font-medium">No instructor payment records found</h3>
                  <p className="text-gray-500 mt-2">
                    When you assign instructors to practical lessons, payment records will appear here.
                  </p>
                </div>
              ) : (
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Instructor</TableHead>
                      <TableHead>Student</TableHead>
                      <TableHead>Course</TableHead>
                      <TableHead>Amount</TableHead>
                      <TableHead>Status</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {instructorPayments.slice(0, 5).map((payment) => (
                      <TableRow key={payment.id}>
                        <TableCell>{payment.instructorName}</TableCell>
                        <TableCell>{payment.studentName}</TableCell>
                        <TableCell>{payment.courseName}</TableCell>
                        <TableCell>
                          <div className="text-sm">
                            <div>${payment.amountPaid.toFixed(2)}</div>
                            <div className="text-gray-500">of ${payment.totalAmount.toFixed(2)}</div>
                          </div>
                        </TableCell>
                        <TableCell>
                          <Badge className={getInstructorStatusColor(payment.paymentStatus)}>
                            {payment.paymentStatus.replace('_', ' ')}
                          </Badge>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              )}
            </CardContent>
            <div className="px-6 py-4 border-t">
              <Button variant="outline" className="w-full" onClick={() => navigate("/admin-dashboard/instructor-payments")}>
                View All Instructor Payments
              </Button>
            </div>
          </Card>
        </TabsContent>

        <TabsContent value="reports" className="space-y-6">
          <Card>
            <CardHeader>
              <CardTitle>Financial Reports</CardTitle>
              <CardDescription>
                Generate and download financial reports
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-6">
              <div className="grid gap-6 grid-cols-1 md:grid-cols-2 lg:grid-cols-3">
                <Card>
                  <CardHeader>
                    <CardTitle className="text-lg">Revenue Report</CardTitle>
                  </CardHeader>
                  <CardContent>
                    <p className="text-sm text-gray-500 mb-4">
                      Comprehensive breakdown of all revenue streams and financial activity.
                    </p>
                    <Button className="w-full">
                      <FileText className="h-4 w-4 mr-2" />
                      Generate Report
                    </Button>
                  </CardContent>
                </Card>
                
                <Card>
                  <CardHeader>
                    <CardTitle className="text-lg">Transaction Report</CardTitle>
                  </CardHeader>
                  <CardContent>
                    <p className="text-sm text-gray-500 mb-4">
                      Detailed list of all transactions with filtering options by date range.
                    </p>
                    <Button className="w-full">
                      <FileText className="h-4 w-4 mr-2" />
                      Generate Report
                    </Button>
                  </CardContent>
                </Card>
                
                <Card>
                  <CardHeader>
                    <CardTitle className="text-lg">Package Sales Report</CardTitle>
                  </CardHeader>
                  <CardContent>
                    <p className="text-sm text-gray-500 mb-4">
                      Analysis of lesson package sales and popularity metrics.
                    </p>
                    <Button className="w-full">
                      <FileText className="h-4 w-4 mr-2" />
                      Generate Report
                    </Button>
                  </CardContent>
                </Card>
              </div>
              
              <Card>
                <CardHeader>
                  <CardTitle className="text-lg">Custom Report</CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="grid gap-4 md:grid-cols-3">
                    <div className="space-y-2">
                      <Label htmlFor="report-type">Report Type</Label>
                      <select
                        id="report-type"
                        className="w-full border-gray-300 rounded-md shadow-sm focus:border-blue-300 focus:ring focus:ring-blue-200 focus:ring-opacity-50"
                      >
                        <option value="financial">Financial Summary</option>
                        <option value="transactions">Transactions</option>
                        <option value="packages">Package Sales</option>
                        <option value="students">Student Payments</option>
                        <option value="instructors">Instructor Payments</option>
                      </select>
                    </div>
                    
                    <div className="space-y-2">
                      <Label htmlFor="start-date">Start Date</Label>
                      <Input
                        id="start-date"
                        type="date"
                      />
                    </div>
                    
                    <div className="space-y-2">
                      <Label htmlFor="end-date">End Date</Label>
                      <Input
                        id="end-date"
                        type="date"
                      />
                    </div>
                  </div>
                  
                  <Button className="mt-4">
                    <FileText className="h-4 w-4 mr-2" />
                    Generate Custom Report
                  </Button>
                </CardContent>
              </Card>
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>
    </DashboardLayout>
  );
};

export default FinanceManagement;
